import { Fragment, type ReactNode } from "react";
import { Link } from "wouter";

export type InlineSegment =
  | string
  | { link: { href: string; label: string } };

export type ListEntry = string | { text: string; items?: ListEntry[] };

export type Block =
  | { p: string }
  | { strong: string }
  | { h3: string }
  | { ul: ListEntry[] }
  | { ol: ListEntry[] }
  | { note: string }
  | { img: { src: string; alt: string; caption?: string } };

export type Section = {
  id: string;
  title: string;
  body: Block[];
};

const INTERNAL_PREFIXES = [
  "/policies",
  "/privacy",
  "/terms",
  "/faq-procedures",
  "/faq-renter",
  "/help",
  "/insurance",
  "/how-it-works",
  "/about",
  "/search",
  // GEO + Guide-Hub editorial surfaces — keep cross-links SPA-native so
  // internal navigation (and crawl paths) stay within the app shell.
  "/models",
  "/cities",
  "/hire",
  "/compare",
  "/guides",
  "/list-your-car",
];

function isInternal(href: string): boolean {
  if (!href) return false;
  return INTERNAL_PREFIXES.some((p) => href === p || href.startsWith(`${p}#`) || href.startsWith(`${p}?`) || href.startsWith(`${p}/`));
}

const LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;
// Mirrors the `**bold**` regex used by the Intercom sync script
// (artifacts/api-server/src/scripts/sync-intercom-faqs.ts) so both surfaces
// render the same spans as bold.
const BOLD_RE = /\*\*([^*]+)\*\*/g;

function parseLinks(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  LINK_RE.lastIndex = 0;
  while ((match = LINK_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      out.push(text.slice(lastIndex, match.index));
    }
    const label = match[1];
    const href = match[2];
    const k = `${keyPrefix}-link-${i++}`;
    if (href.startsWith("mailto:") || href.startsWith("http://") || href.startsWith("https://")) {
      out.push(
        <a
          key={k}
          href={href}
          target={href.startsWith("http") ? "_blank" : undefined}
          rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
          className="text-primary underline underline-offset-2 hover:no-underline"
        >
          {label}
        </a>,
      );
    } else if (isInternal(href)) {
      out.push(
        <Link key={k} href={href} className="text-primary underline underline-offset-2 hover:no-underline">
          {label}
        </Link>,
      );
    } else {
      out.push(
        <a key={k} href={href} className="text-primary underline underline-offset-2 hover:no-underline">
          {label}
        </a>,
      );
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    out.push(text.slice(lastIndex));
  }
  return out;
}

export function parseInline(text: string, keyPrefix: string): ReactNode {
  const out: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  BOLD_RE.lastIndex = 0;
  while ((match = BOLD_RE.exec(text)) !== null) {
    if (match.index > lastIndex) {
      out.push(...parseLinks(text.slice(lastIndex, match.index), `${keyPrefix}-t${i}`));
    }
    out.push(
      <strong key={`${keyPrefix}-b${i++}`} className="font-semibold text-foreground">
        {parseLinks(match[1], `${keyPrefix}-b${i}`)}
      </strong>,
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    out.push(...parseLinks(text.slice(lastIndex), `${keyPrefix}-tail`));
  }
  return out.length === 1 ? out[0] : <>{out.map((n, idx) => <Fragment key={idx}>{n}</Fragment>)}</>;
}

function renderListEntry(entry: ListEntry, key: string, vars: Record<string, string>): ReactNode {
  if (typeof entry === "string") {
    return <li key={key}>{parseInline(interpolate(entry, vars), key)}</li>;
  }
  return (
    <li key={key}>
      {parseInline(interpolate(entry.text, vars), key)}
      {entry.items && entry.items.length > 0 && (
        <ul className="mt-2 list-[circle] space-y-2 pl-6">
          {entry.items.map((sub, i) => renderListEntry(sub, `${key}-${i}`, vars))}
        </ul>
      )}
    </li>
  );
}

export function interpolate(text: string, vars: Record<string, string>): string {
  return text.replace(/\{\{(\w+)\}\}/g, (_, k) => vars[k] ?? `{{${k}}}`);
}

function stripMarkdownLinks(text: string): string {
  return text.replace(LINK_RE, (_m, label) => label);
}

function listEntryToText(entry: ListEntry, vars: Record<string, string>): string {
  if (typeof entry === "string") return stripMarkdownLinks(interpolate(entry, vars));
  const head = stripMarkdownLinks(interpolate(entry.text, vars));
  const children = entry.items?.map((c) => listEntryToText(c, vars)).join(" ") ?? "";
  return children ? `${head} ${children}` : head;
}

export function blocksToPlainText(blocks: Block[], vars: Record<string, string>): string {
  return blocks
    .map((b) => {
      if ("p" in b) return stripMarkdownLinks(interpolate(b.p, vars));
      if ("strong" in b) return stripMarkdownLinks(interpolate(b.strong, vars));
      if ("h3" in b) return stripMarkdownLinks(interpolate(b.h3, vars));
      if ("note" in b) return stripMarkdownLinks(interpolate(b.note, vars));
      if ("img" in b) {
        const caption = b.img.caption ? stripMarkdownLinks(interpolate(b.img.caption, vars)) : "";
        const alt = interpolate(b.img.alt, vars);
        return caption ? `${alt} ${caption}` : alt;
      }
      if ("ul" in b) return b.ul.map((e) => listEntryToText(e, vars)).join(" ");
      if ("ol" in b) return b.ol.map((e) => listEntryToText(e, vars)).join(" ");
      return "";
    })
    .filter(Boolean)
    .join(" ");
}

export function renderBlocks(blocks: Block[], keyPrefix: string, vars: Record<string, string>): ReactNode[] {
  return blocks.map((block, i) => {
    const k = `${keyPrefix}-${i}`;
    if ("p" in block) {
      return <p key={k} className="mt-3 leading-relaxed">{parseInline(interpolate(block.p, vars), k)}</p>;
    }
    if ("strong" in block) {
      return (
        <p key={k} className="mt-4 font-semibold text-foreground">
          {parseInline(interpolate(block.strong, vars), k)}
        </p>
      );
    }
    if ("h3" in block) {
      return (
        <h3 key={k} className="mt-6 text-base font-bold text-foreground">
          {parseInline(interpolate(block.h3, vars), k)}
        </h3>
      );
    }
    if ("note" in block) {
      return (
        <p key={k} className="mt-3 rounded-lg border border-border bg-muted/50 p-4 text-sm leading-relaxed">
          {parseInline(interpolate(block.note, vars), k)}
        </p>
      );
    }
    if ("img" in block) {
      const base = ((import.meta.env.BASE_URL as string | undefined) || "/").replace(/\/$/, "");
      const rawSrc = block.img.src;
      const src = /^(https?:)?\/\//.test(rawSrc) || rawSrc.startsWith("data:")
        ? rawSrc
        : `${base}/${rawSrc.replace(/^\//, "")}`;
      return (
        <figure key={k} className="mt-5">
          <img
            src={src}
            alt={interpolate(block.img.alt, vars)}
            loading="lazy"
            className="w-full rounded-xl border border-border bg-muted"
          />
          {block.img.caption && (
            <figcaption className="mt-2 text-sm text-muted-foreground">
              {parseInline(interpolate(block.img.caption, vars), k)}
            </figcaption>
          )}
        </figure>
      );
    }
    if ("ul" in block) {
      return (
        <ul key={k} className="mt-3 list-disc space-y-2 pl-6 leading-relaxed">
          {block.ul.map((entry, idx) => renderListEntry(entry, `${k}-${idx}`, vars))}
        </ul>
      );
    }
    if ("ol" in block) {
      return (
        <ol key={k} className="mt-3 list-decimal space-y-2 pl-6 leading-relaxed">
          {block.ol.map((entry, idx) => renderListEntry(entry, `${k}-${idx}`, vars))}
        </ol>
      );
    }
    return null;
  });
}
